// The email box on a Launch pad card (owner request 2026-10-24): the same call as the Notify me dialog (ns-notify.js), made from the card.
// It posts { variantId, email, consent, locale } to the campaign Worker through the app proxy and never puts the address in a URL. A sign-up
// is remembered by variant id only (no email) so the card keeps saying "You're on the list" after a reload.
// The body and the answer rules are ns-notify.js's own, copied here: importing that module would need an import-map line in the layout, and the
// card page keeps its own listeners apart from the dialog's.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function notifyBody({ variantId, email, consent, locale }) {
  const variant = String(variantId ?? '');
  const value = String(email ?? '').trim();
  if (!EMAIL.test(value) || value.length > 254) return { ok: false, error: 'email' };
  if (consent !== true) return { ok: false, error: 'consent' };
  if (!/^\d{1,20}$/.test(variant)) return { ok: false, error: 'server' };
  return { ok: true, body: { variantId: variant, email: value, consent: true, locale: String(locale || 'en').slice(0, 12) } };
}
export function notifyView(status, json) {
  if (status >= 200 && status < 300 && json?.saved === true) return 'done';
  if (status === 409 && json?.reason === 'available') return 'available';
  if (status === 503 && json?.error === 'not_ready') return 'unavailable';
  return 'error';
}

const KEY = 'ns-notified-variants';
const read = storage => { try { const list = JSON.parse(storage.getItem(KEY) ?? '[]'); return Array.isArray(list) ? list.map(String) : []; } catch { return []; } };
export const remember = (storage, variantId) => { try { storage.setItem(KEY, JSON.stringify([...new Set([...read(storage), String(variantId)])].slice(-200))); } catch { /* private mode: the card still shows done now */ } };
export const remembered = (storage, variantId) => read(storage).includes(String(variantId));

// A Launch pad card (data-ns-popover-card) keeps its form in a sheet over the card: saved, the sheet closes and the card's one button reads
// "We'll remind you" (data-done-label), disabled, with the count beside it only when it is real. A collection card swaps its form for the message.
function finish(form) {
  const card = form.closest?.('[data-ns-popover-card]');
  if (card) {
    const button = card.querySelector('[data-ns-popover-open]');
    const label = button?.querySelector('[data-ns-cta-label]');
    closeSheet(card, false);
    card.dataset.notified = 'true';
    if (button && label) {
      label.textContent = button.dataset.doneLabel;
      button.disabled = true;
      button.setAttribute('aria-expanded', 'false');
      button.dataset.notified = 'true';
      const icon = button.querySelector('.ns-cs-card__cta-icon');
      const check = button.querySelector('.ns-cs-card__cta-check');
      if (icon) icon.hidden = true;
      if (check) check.hidden = false;
    }
    return;
  }
  const done = form.parentElement.querySelector(`[data-ns-inline-done="${form.dataset.variant}"]`);
  form.hidden = true;
  if (done) done.hidden = false;
}

export function openSheet(card) {
  const sheet = card.querySelector('.ns-cs-card__sheet');
  const button = card.querySelector('[data-ns-popover-open]');
  if (!sheet || !button || button.disabled) return;
  // One sheet at a time on the page.
  for (const other of card.ownerDocument.querySelectorAll('[data-ns-popover-card][data-open]')) if (other !== card) closeSheet(other, false);
  sheet.hidden = false;
  card.dataset.open = 'true';
  button.setAttribute('aria-expanded', 'true');
  sheet.querySelector('input[name="email"]')?.focus({ preventScroll: true });
}

export function closeSheet(card, returnFocus = true) {
  const sheet = card.querySelector('.ns-cs-card__sheet');
  const button = card.querySelector('[data-ns-popover-open]');
  if (!sheet || sheet.hidden) return;
  sheet.hidden = true;
  delete card.dataset.open;
  button?.setAttribute('aria-expanded', 'false');
  if (returnFocus) button?.focus({ preventScroll: true });
}

// The button opens or closes its card's sheet; Esc or the close button shuts it and gives the focus back to the button; a tap outside shuts it too.
export function startPopovers(root = document) {
  root.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const open = target?.closest('[data-ns-popover-open]');
    if (open) {
      const card = open.closest('[data-ns-popover-card]');
      if (card?.dataset.open) closeSheet(card); else if (card) openSheet(card);
      return;
    }
    const close = target?.closest('[data-ns-popover-close]');
    if (close) { closeSheet(close.closest('[data-ns-popover-card]')); return; }
    for (const card of root.querySelectorAll('[data-ns-popover-card][data-open]')) if (!card.contains(target)) closeSheet(card, false);
  });
  root.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    for (const card of root.querySelectorAll('[data-ns-popover-card][data-open]')) closeSheet(card);
  });
}

export function startInlineNotify(root = document, storage = globalThis.localStorage) {
  for (const form of root.querySelectorAll('[data-ns-inline-notify]')) {
    if (storage && remembered(storage, form.dataset.variant)) { finish(form); continue; }
    let pending = false;
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const error = form.querySelector('[data-ns-inline-error]');
      const submit = form.querySelector('[type="submit"]');
      // A robot fills the hidden field; a person never sees it. The card answers as if it worked and sends nothing.
      if (form.elements.website?.value) { finish(form); return; }
      const checked = notifyBody({ variantId: form.dataset.variant, email: form.elements.email.value, consent: form.elements.consent.checked, locale: (root.ownerDocument ?? root).documentElement?.lang });
      if (!checked.ok) { error.textContent = error.dataset[checked.error]; error.hidden = false; form.elements[checked.error]?.focus(); return; }
      if (pending) return;
      pending = true; submit.disabled = true; error.hidden = true;
      let view = 'error';
      try {
        const response = await fetch(form.dataset.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(checked.body) });
        view = notifyView(response.status, await response.json().catch(() => null));
      } catch { /* the error line below */ } finally { pending = false; submit.disabled = false; }
      if (view === 'done') { if (storage) remember(storage, form.dataset.variant); finish(form); form.dispatchEvent?.(new CustomEvent('ns:notified', { bubbles: true, detail: { variantId: form.dataset.variant } })); return; }
      error.textContent = error.dataset[view === 'available' ? 'available' : view === 'unavailable' ? 'unavailable' : 'server'];
      error.hidden = false;
    });
  }
}

if (typeof document !== 'undefined') { startInlineNotify(document); startPopovers(document); }
